import React from 'react';
import { SchematicShell, Rect, Line } from './SchematicPrimitives';
import {
  CHAIR,
  clampSpec,
} from '../../3d/geometry/proceduralRenderers/proceduralSpecs';

/**
 * Elevation schematic for `chair` — the Track-Arm dining chair.
 *
 * Matches `chairRenderer` in furnitureRenderers.js (shared proportions live in
 * `proceduralRenderers/proceduralSpecs.js` → CHAIR): a slim seat pad on thin
 * matte-black metal legs where the two REAR legs continue full-height as posts
 * that carry a slightly reclined backrest (not a floating panel).
 *
 * Front: two full-height uprights (rear posts / front legs coincide in
 * elevation), a backrest panel between them up top, a slim seat slab.
 * Side: rear post full-height at the back, a short front leg, seat slab front
 * to back, backrest panel up top. Right view mirrors left.
 *
 * The seat is drawn to the full footprint width/depth (the 2D elevation shows
 * the nominal footprint); the 3D seat is inset by CHAIR.seatWidthFrac /
 * seatDepthFrac. That small inset stays within the sync test's bbox tolerance.
 */

const seatHeightFromFloor = (height) =>
  clampSpec(
    height * CHAIR.seatHeightFrac,
    CHAIR.seatHeightMin,
    CHAIR.seatHeightMax
  );

const backPanelHeight = (height, seatTopFromFloor) =>
  clampSpec(
    (height - seatTopFromFloor) * CHAIR.backPanelHeightFrac,
    CHAIR.backPanelHeightMin,
    CHAIR.backPanelHeightMax
  );

export function ChairSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  strokeColor,
  strokeWidth,
}) {
  if (view === 'left' || view === 'right') {
    return (
      <SchematicShell
        width={depth}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
        drawFrame={false}
      >
        <g
          transform={
            view === 'right' ? `scale(-1, 1) translate(${-depth}, 0)` : ''
          }
        >
          <SideProfile
            depth={depth}
            height={height}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth}
          />
        </g>
      </SchematicShell>
    );
  }

  // FRONT
  const seatH = seatHeightFromFloor(height);
  const seatTopY = height - seatH;
  const seatThick = CHAIR.seatThick;
  const seatBottomY = seatTopY + seatThick;
  const legR = CHAIR.legRadius;
  const legW = 2 * legR;
  // Upright centers mirror the renderer (seat half-extent minus the post inset).
  const lx = (width * CHAIR.seatWidthFrac) / 2 - CHAIR.postInset;
  const postCx = [width / 2 - lx, width / 2 + lx];
  const backH = backPanelHeight(height, seatH);
  const backTopY = CHAIR.backPanelTopGap;

  return (
    <SchematicShell
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
      drawFrame={false}
    >
      {/* Two full-height frame uprights (rear posts; front legs coincide). */}
      {postCx.map((cx, i) => (
        <Rect
          key={`upright-${i}`}
          x={cx - legR}
          y={0}
          width={legW}
          height={height}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      ))}
      {/* Backrest panel between the posts' INNER edges — sharing the
          posts' edges instead of overlapping them, so no strokes cross. */}
      <Rect
        x={postCx[0] + legR}
        y={backTopY}
        width={postCx[1] - postCx[0] - 2 * legR}
        height={backH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Slim seat slab across the footprint — white-filled: the seat is
          in FRONT of the rear posts, so it occludes them where it crosses
          (painter's order, same trick as the cooktop knobs). */}
      <Rect
        x={0}
        y={seatTopY}
        width={width}
        height={seatThick}
        fill="white"
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </SchematicShell>
  );
}

function SideProfile({ depth, height, strokeColor, strokeWidth }) {
  const seatH = seatHeightFromFloor(height);
  const seatTopY = height - seatH;
  const seatThick = CHAIR.seatThick;
  const seatBottomY = seatTopY + seatThick;
  const legR = CHAIR.legRadius;
  const legW = 2 * legR;
  const lz = (depth * CHAIR.seatDepthFrac) / 2 - CHAIR.postInset;
  const frontCx = depth / 2 - lz; // front of the chair, near edge
  const backCx = depth / 2 + lz; // back of the chair (carries the post)
  const backH = backPanelHeight(height, seatH);
  const backTopY = CHAIR.backPanelTopGap;

  return (
    <g>
      {/* Rear post — full height at the back. */}
      <Rect
        x={backCx - legR}
        y={0}
        width={legW}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Backrest panel up top, slightly reclined toward the back. */}
      <Rect
        x={backCx - legR}
        y={backTopY}
        width={legW}
        height={backH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={backCx}
        y1={backTopY}
        x2={backCx - 1.5 * legR}
        y2={backTopY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Seat slab front-to-back across the depth. */}
      <Rect
        x={0}
        y={seatTopY}
        width={depth}
        height={seatThick}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Short front leg below the seat. */}
      <Rect
        x={frontCx - legR}
        y={seatBottomY}
        width={legW}
        height={height - seatBottomY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </g>
  );
}

ChairSchematic.supportsSideViews = true;
